import { DndContext, DragOverlay } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ChatPauseIcon,
  ChatPlayIcon,
  Chevron,
  Disclaimer,
  Div,
  DragSortIcon,
  EditPenIcon,
  H5,
  Icon,
  ImagePlaceholderCircle,
  ImagePlaceholderIcon,
  Img,
  P,
  Pill,
  TaxRatesIcon,
  TooltipWrapper,
  TrashIconRed,
  centsToDollars,
  formatter,
  lightTheme,
} from '@vgrubs/components';
import { TaxRatesOverriddenPill } from '../../taxRates/TaxRatesSection';
import { CategorySchedulePills } from '../Categories/CategorySchedulePills';
import { useEffect, useRef, useState } from 'react';
import { Collapsible, ItemListImage } from '../../MenyStyled';
import { useSelectedMenu } from '../../menuSelection';
import {
  Availability,
  CategoryV2Response,
  MenuItemV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { pausedItemLabel } from './itemPause.helpers';

type ItemClick = (data: {
  item: MenuItemV2Response;
  category: CategoryV2Response | MenuItemV2Response;
}) => void;

/**
 * An item with a `linkKey` is the same product in another menu of the brand
 * (menus made as a copy stay linked). Availability travels between them, so
 * the person marking it sold out here should know it changes elsewhere too.
 *
 * The key outlives the copy: after the linked menu is deleted the backend
 * keeps it on the original, so the key alone would flag every item of a
 * brand that once had a second menu. Hence the note needs a second menu too.
 */
export const LinkedItemNote = () => {
  const { menus } = useSelectedMenu();
  if (menus.length < 2) return null;
  return (
    <TooltipWrapper tooltip="This item also exists in other menus of this brand. Marking it sold out or available changes it there too.">
      <Disclaimer $m="0" $color={lightTheme.darkGray}>
        Also in other menus
      </Disclaimer>
    </TooltipWrapper>
  );
};

export const MenuItemTypeComponent = ({
  item,
  onClick,
  onClickDelete,
  menu,
  isSorting,
  activeId,
  activeCategoryId,
  onDragStart,
  onDragEnd,
  isPos,
  handleEditTaxRates,
  onClickPause,
  onClickResume,
  timeZone,
}: {
  item: CategoryV2Response;
  onClick?: (data: {
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  }) => void;
  onClickDelete?: (data: {
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  }) => void;
  menu: MenuV2Response | null;
  isSorting: boolean;
  activeId: number | null;
  activeCategoryId: number | null;
  onDragStart: (event: any, categoryId: number) => void;
  onDragEnd: (event: any, categoryId: number) => void;
  isPos: boolean;
  /** Absent when taxes cannot be set here - partner app, or a POS location. */
  handleEditTaxRates?: (item: MenuItemV2Response) => void;
  /** Absent on a POS location, where the register owns availability. */
  onClickPause?: ItemClick;
  onClickResume?: ItemClick;
  timeZone?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (ref.current) setHeight(ref?.current?.clientHeight);
  }, [menu]);

  useEffect(() => {
    if (ref.current && isSorting) {
      setExpanded(true);
      setHeight(ref?.current?.clientHeight);
    }
  }, [isSorting]);

  if (!item) return null;

  return (
    <div>
      <Div
        $box
        $p="16px 24px"
        $flex
        $alignCenter
        $between
        $mb="8"
        $background={expanded && lightTheme.primary}
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
      >
        <Div>
          <P $bold $m="0" $color={expanded && lightTheme.white}>
            {item.name} ({item.items?.length || 0})
          </P>
          <CategorySchedulePills
            schedules={item.schedules}
            inverted={expanded}
          />
        </Div>
        <Icon
          $pointer
          $rotate={expanded ? '360deg' : '180deg'}
          color={expanded ? lightTheme.white : lightTheme.darkGray}
        >
          <Chevron />
        </Icon>
      </Div>

      <Collapsible expanded={expanded} elementHeight={height}>
        <div ref={ref}>
          <DndContext
            onDragEnd={(e) => onDragEnd(e, item.id)}
            onDragStart={(e) => onDragStart(e, item.id)}
          >
            <SortableContext
              items={item.items?.map((task) => task.id) || []}
              strategy={verticalListSortingStrategy}
            >
              {item.items?.map((itemItem) => {
                return (
                  <MenuItemComponent
                    item={itemItem}
                    category={item}
                    key={itemItem.id}
                    onClick={onClick}
                    onClickDelete={onClickDelete}
                    isSorting={isSorting}
                    isPos={isPos}
                    handleEditTaxRates={handleEditTaxRates}
                    onClickPause={onClickPause}
                    onClickResume={onClickResume}
                    timeZone={timeZone}
                  />
                );
              })}
            </SortableContext>
            <DragOverlay>
              {activeId && activeCategoryId === item.id ? (
                <MenuItemComponent
                  item={
                    item.items?.find(
                      (t) => activeId === t.id
                    ) as MenuItemV2Response
                  }
                  category={item}
                  isSorting={isSorting}
                  isPos={isPos}
                  timeZone={timeZone}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
          {item.items?.length === 0 ? (
            <P $m="0" $center>
              No items
            </P>
          ) : null}
        </div>
      </Collapsible>
    </div>
  );
};

export const MenuItemComponent = ({
  item,
  onClick,
  onClickDelete,
  key,
  category,
  isSorting,
  isPos,
  handleEditTaxRates,
  onClickPause,
  onClickResume,
  timeZone,
}: {
  item: MenuItemV2Response;
  onClick?: (data: {
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  }) => void;
  onClickDelete?: (data: {
    item: MenuItemV2Response;
    category: CategoryV2Response | MenuItemV2Response;
  }) => void;
  key?: number;
  category: CategoryV2Response | MenuItemV2Response;
  isSorting: boolean;
  isPos: boolean;
  handleEditTaxRates?: (item: MenuItemV2Response) => void;
  onClickPause?: ItemClick;
  onClickResume?: ItemClick;
  timeZone?: string;
}) => {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
  });

  const styles = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 0,
    opacity: isDragging ? 0.3 : 1,
  };
  const isSoldOut = item.availability === Availability.SoldOut;

  const SoldOutPill = () => {
    return (
      <Pill
        $background={lightTheme.primary}
        $minWidth="170px"
        $minWidthSM="150px"
      >
        <Disclaimer
          $m="0"
          $color={lightTheme.white}
          style={{ whiteSpace: 'nowrap' }}
        >
          {pausedItemLabel(item, timeZone)}
        </Disclaimer>
      </Pill>
    );
  };

  return (
    <Div
      ref={setNodeRef}
      style={styles}
      {...attributes}
      key={key}
      $box
      $p="12px 24px "
      $flex
      $alignCenter
      $between
      $mb="8"
      $width="100%"
    >
      <Div
        $width="100%"
        $flex
        $alignCenter
        $gap="16px"
        onClick={() => {
          if (!isSorting) {
            onClick?.({ item, category });
          }
        }}
        {...(isSorting ? listeners : {})}
        style={{
          cursor: isSorting ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
        }}
      >
        {isSorting ? (
          <Icon>
            <DragSortIcon />
          </Icon>
        ) : null}
        <Div $flex>
          {item.imageUrl ? (
            <ItemListImage>
              <Img
                alt=""
                height="50px"
                src={
                  item.imageUrl ? item.imageUrl : (ImagePlaceholderIcon as any)
                }
              />
            </ItemListImage>
          ) : (
            <ImagePlaceholderCircle />
          )}
        </Div>

        <Div $flex $column $gap="2px" $alignStart>
          <P $mb="0">{item.name}</P>
          <H5 $m="0" $color={lightTheme.primary}>
            {formatter.format(centsToDollars(item.price ?? 0))}
          </H5>
          {item.linkKey && <LinkedItemNote />}
          <TaxRatesOverriddenPill overridden={!!item.taxRatesOverridden} />
          {isSoldOut && (
            <Div $mobile>
              <SoldOutPill />
            </Div>
          )}
        </Div>
      </Div>
      <Div $flex $alignCenter $between $gap="16px" $gapSM="8px">
        {isSoldOut && (
          <Div $desktop>
            <SoldOutPill />
          </Div>
        )}
        {/* Only once the backend sends the field - it is absent on a
        deployment without the tax rates release. */}
        {handleEditTaxRates && item.taxRates && (
          <Icon
            $shadow
            $pointer
            $background={lightTheme.white}
            $backgroundSize={35}
            onClick={() => handleEditTaxRates(item)}
          >
            <TaxRatesIcon color={lightTheme.primary} />
          </Icon>
        )}
        {isSoldOut && onClickResume && (
          <Icon
            $shadow
            $pointer
            $background={lightTheme.white}
            $backgroundSize={35}
            role="button"
            aria-label="Resume item"
            title="Resume item"
            onClick={() => onClickResume({ item, category })}
          >
            <ChatPlayIcon color={lightTheme.primary} />
          </Icon>
        )}
        {!isSoldOut && onClickPause && (
          <Icon
            $shadow
            $pointer
            $background={lightTheme.white}
            $backgroundSize={35}
            role="button"
            aria-label="Pause item"
            title="Pause item"
            onClick={() => onClickPause({ item, category })}
          >
            <ChatPauseIcon color={lightTheme.primary} />
          </Icon>
        )}
        {!isPos && (
          <Icon $shadow $pointer onClick={() => onClick?.({ item, category })}>
            <EditPenIcon />
          </Icon>
        )}
        <Icon $pointer onClick={() => onClickDelete?.({ item, category })}>
          <TrashIconRed />
        </Icon>
      </Div>
    </Div>
  );
};
